import React from "react";
import styled from "styled-components";

import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CDRReviewInformation, reviewSchema } from "./schema";

import Button from "../../themed/button";
import CreatableMultiselectDropdown from "../../themed/multiselect/creatable";
import DatePickerInput from "../../themed/date-picker";
import TextEditor from "../../themed/text-editor";


interface Props {
    reviewInformation?: CDRReviewInformation;
    onSaveReviewInformation: (information: CDRReviewInformation) => void;
}

const Signature = styled.div`
    font-family: 'Allison', cursive;
    white-space: nowrap;
    font-size: 1.25rem;
    padding: 0.75rem;
    line-height: 1rem;
`;

const SignatureLabel = styled.p`
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    color: #7a869a;
    text-transform: capitalize;
    margin-bottom: 0.5rem;
`;

const CDRReviewAndApproval: React.FC<Props> = ({ reviewInformation, onSaveReviewInformation }) => {
    const { control, formState, handleSubmit, watch } = useForm<CDRReviewInformation>({
        resolver: zodResolver(reviewSchema),
        defaultValues: reviewInformation,
        mode: "all"
    });

    const [onSiteEngineerName, reportReviewerName, mciLeaderName] = useWatch({
        control,
        name: ["onSiteEngineerName.value", "reportReviewerName.value", "mciLeaderName.value"],
    });

    return ( 
        <form onSubmit={handleSubmit(onSaveReviewInformation)}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="onSiteEngineerName"
                            render={({ field }) => (
                                <CreatableMultiselectDropdown
                                    label="Onsite Inspection Engineer"
                                    placeholder="Select Onsite Inspection Engineer"
                                    isMulti={false}
                                    isLoading={false}
                                    closeMenuOnSelect
                                    isSearchable
                                    options={approvers}
                                    onSelect={(option) => field.onChange(option)}
                                    selectedValues={field.value}
                                    error={formState.errors.onSiteEngineerName ? formState.errors.onSiteEngineerName.message : ''}
                                />
                            )}
                        />
                    </div>

                    {onSiteEngineerName && (
                        <div className="col">
                            <div>
                                <SignatureLabel>
                                    Onsite Inspection Engineer Signature:
                                </SignatureLabel>

                                <Signature className="text-left bg-light rounded-1 border text-secondary fw-bold">
                                    {onSiteEngineerName}
                                </Signature>
                            </div>
                        </div>
                    )}

                    <div className="col">
                        <Controller
                            control={control}
                            name="onSiteReviewDate"
                            render={({ field }) => (
                                <DatePickerInput
                                    label="Date of Onsite Review"
                                    placeholderText="Select Date of Onsite Review"
                                    date={field.value}
                                    onDateChange={field.onChange}
                                    maxDate={new Date()}
                                    error={formState.errors.onSiteReviewDate ? formState.errors.onSiteReviewDate.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="reportReviewerName"
                            render={({ field }) => (
                                <CreatableMultiselectDropdown
                                    label="Report Reviewer"
                                    placeholder="Select Report Reviewer"
                                    isMulti={false}
                                    isLoading={false}
                                    closeMenuOnSelect
                                    isSearchable
                                    options={[]}
                                    onSelect={(option) => field.onChange(option)}
				                    selectedValues={field.value}
                                    error={formState.errors.reportReviewerName ? formState.errors.reportReviewerName.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    {reportReviewerName && (
                        <div className="col">
                            <div>
                                <SignatureLabel>
                                    Report Reviewer Signature:
                                </SignatureLabel>

                                <Signature className="text-left bg-light rounded-1 border text-secondary fw-bold">
                                    {watch("reportReviewerName.value") || "N/A"}
                                </Signature>
                            </div>
                        </div>
                    )}
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="reportReviewDate"
                            render={({ field }) => (
                                <DatePickerInput
                                    label="Date of Report Review"
                                    placeholderText="Select Date of Report Review"
                                    date={field.value}
                                    onDateChange={field.onChange}
                                    maxDate={new Date()}
                                    error={formState.errors.reportReviewDate ? formState.errors.reportReviewDate.message : ''}
                                />
                            )}
                        />
                    </div>

                    <div className="col">
                        <Controller
                            control={control}
                            name="reportReviewerRecommendation"
                            render={({ field }) => (
                                <TextEditor
                                    name={field.name}
                                    label="Report Reviewer Recommendation"
                                    placeholder="Enter Report Reviewer Recommendation"
                                    error={formState.errors.reportReviewerRecommendation ? formState.errors.reportReviewerRecommendation.message : ''}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                </div>
                
                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="mciLeaderName"
                            render={({ field }) => (
                                <CreatableMultiselectDropdown
                                    label="MCI Leader"
                                    placeholder="Select MCI Leader"
                                    isMulti={false}
                                    isLoading={false}
                                    closeMenuOnSelect
                                    isSearchable
                                    options={[]}
                                    onSelect={(option) => field.onChange(option)}
				                    selectedValues={field.value}
                                    error={formState.errors.mciLeaderName ? formState.errors.mciLeaderName.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    {mciLeaderName && (
                        <div className="col">
                            <div>
                                <SignatureLabel>
                                    MCI Leader Signature:
                                </SignatureLabel>

                                <Signature className="text-left bg-light rounded-1 border text-secondary fw-bold">
                                    {watch("mciLeaderName.value") || "N/A"}
                                </Signature>
                            </div>
                        </div>
                    )}
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="mciReviewDate"
                            render={({ field }) => (
                                <DatePickerInput
                                    label="Date of MCI Leader Review"
                                    placeholderText="Select Date of MCI Leader Review"
                                    date={field.value}
                                    onDateChange={field.onChange}
                                    maxDate={new Date()}
                                    error={formState.errors.mciReviewDate ? formState.errors.mciReviewDate.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-content-end align-item-center">
                    <Button type="submit" label={formState.isSubmitting ? "Saving Information..." : "Save Information"} />
                </div>
            </div>
        </form>
    );
};

const approvers = [
    {
        label: 'Covenant Oriaku (covenant.oriaku@shellnigeria.com)',
        value: 'Covenant Oriaku',
        email: 'covenant.oriaku@shellnigeria.com',
    },
    {
        label: 'Nnamdi Nwamaghinna (nnamdi.nwamaghinna@shell.com)',
        value: 'Nnamdi Nwamaghinna',
        email: 'nnamdi.Nwamaghinna@shell.com',
    },
    {
        label: 'Tobechukwu Obi (tobechukwu.obi@shell.com)',
        value: 'Tobechukwu Obi',
        email: 'tobechukwu.obi@shell.com',
    },
]

export default CDRReviewAndApproval;